import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  hospitalAdminApi,
  type RoundRobinAssignee,
  type SarvamVoiceAiSettings,
} from "@/api/hospitalAdmin";
import { toast } from "sonner";

type Props = {
  settings: SarvamVoiceAiSettings | undefined;
  isLoading: boolean;
  salesAssignees: RoundRobinAssignee[];
};

function eligibleAssignees(assignees: RoundRobinAssignee[]): RoundRobinAssignee[] {
  return assignees.filter(
    (a) => a.status === "ACTIVE" && a.isLeadRoundRobinActive && !a.exitedAt
  );
}

export function SarvamOpdLeadAssignCard({ settings, isLoading, salesAssignees }: Props) {
  const qc = useQueryClient();
  const pool = useMemo(() => eligibleAssignees(salesAssignees), [salesAssignees]);
  const allIds = useMemo(() => pool.map((a) => a.id), [pool]);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!settings || initialized) return;
    const saved = settings.sarvamOpdAssignStaffIdsAllowlist ?? [];
    if (saved.length === 0) {
      setSelectedIds(allIds);
    } else {
      setSelectedIds(saved.filter((id) => allIds.includes(id)));
    }
    setInitialized(true);
  }, [settings, allIds, initialized]);

  useEffect(() => {
    if (!initialized || !settings) return;
    const saved = settings.sarvamOpdAssignStaffIdsAllowlist ?? [];
    if (saved.length === 0) {
      setSelectedIds(allIds);
    } else {
      setSelectedIds(saved.filter((id) => allIds.includes(id)));
    }
  }, [settings?.sarvamOpdAssignStaffIdsAllowlist, allIds, initialized, settings]);

  const toggle = (id: string, checked: boolean) => {
    setSelectedIds((prev) =>
      checked ? [...new Set([...prev, id])] : prev.filter((x) => x !== id)
    );
  };

  const selectAll = () => setSelectedIds([...allIds]);
  const savePayloadIds = (): string[] => {
    if (selectedIds.length === 0) return [];
    if (selectedIds.length === allIds.length && allIds.every((id) => selectedIds.includes(id))) {
      return [];
    }
    return selectedIds;
  };

  const saveMutation = useMutation({
    mutationFn: () =>
      hospitalAdminApi.saveSarvamVoiceAiSettings({
        sarvamOpdAssignStaffIdsAllowlist: savePayloadIds(),
      }),
    onSuccess: async () => {
      toast.success("Sarvam OPD lead assignment saved");
      await qc.invalidateQueries({ queryKey: ["sarvam-voice-ai-settings"] });
    },
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err as Error)?.message ||
        "Failed to save";
      toast.error(message);
    },
  });

  const noneSelected = selectedIds.length === 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Sarvam booked OPD — lead assignment</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          When Sarvam successfully books an OPD, the CRM lead owner is reassigned in round-robin
          order to one of the sales persons selected below (replacing the previous owner). All active
          in-house sales persons are included by default.
        </p>
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading…
          </div>
        ) : pool.length === 0 ? (
          <p className="text-sm text-muted-foreground">No eligible sales persons found.</p>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <Button type="button" variant="outline" size="sm" onClick={selectAll}>
                Select all
              </Button>
              <span className="text-xs text-muted-foreground">
                {selectedIds.length === allIds.length && !noneSelected
                  ? "All sales persons (default)"
                  : `${selectedIds.length} of ${pool.length} selected`}
              </span>
            </div>
            <div className="grid gap-2 sm:grid-cols-2 max-h-48 overflow-y-auto border rounded-md p-3">
              {pool.map((u) => (
                <label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox
                    checked={selectedIds.includes(u.id)}
                    onCheckedChange={(v) => toggle(u.id, v === true)}
                  />
                  <span>{u.name}</span>
                  <span className="text-xs text-muted-foreground truncate">{u.email}</span>
                </label>
              ))}
            </div>
          </>
        )}
        {noneSelected && pool.length > 0 && (
          <p className="text-sm text-amber-700">
            Select at least one sales person — otherwise Sarvam OPD bookings will not reassign leads.
          </p>
        )}
        <Button
          type="button"
          disabled={saveMutation.isPending || noneSelected || pool.length === 0}
          onClick={() => saveMutation.mutate()}
        >
          {saveMutation.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving…
            </>
          ) : (
            "Save assignment pool"
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
